import React, { useMemo } from 'react';

import { CheckboxTick } from '@components/Forms/CheckboxTick';
import { getDecodeResultOr, type MobxZodArrayField } from '@monoid-dev/mobx-zod-form';
import { getForm } from '@monoid-dev/mobx-zod-form-react';
import { type ClassMap, makeMuiStyles, useCommonStyles } from '@styles';
import clsx from 'clsx';
import { observer } from 'mobx-react';
import { z, type ZodString, type ZodArray, ZodEnum } from 'zod';

const useStyles = makeMuiStyles((theme, common) => ({
  checkboxInputContainer: {
    ...common.textGray700,
    ...common.middleText,
    ...common.itemsCenter,
    position: 'relative',
    display: 'inline-flex',
    verticalAlign: 'middle',
    '&:hover': {
      cursor: 'pointer',
    },
    '&$disabled': {
      color: theme.palette.grey['500'],
      '&:hover': {
        cursor: 'not-allowed',
      },
    },
  },
  checkbox: {
    height: 13,
    width: 13,
    zIndex: 99,
    '&$disabled': {
      backgroundColor: theme.palette.grey['100'],
    },
  },
  checkboxHidden: {
    ...common.baseButton,
    pointerEvents: 'none',
    opacity: 0,
    marginLeft: -12,
  },
  disabled: {},
}));

export interface CheckboxInputProps<T extends ZodString | ZodEnum<any>> {
  field: MobxZodArrayField<ZodArray<T>>;
  value: z.infer<T>;
  label: React.ReactNode;
  disabled?: boolean;
  classes?: ClassMap<'container' | 'checkbox'>;
}

export const CheckboxInput = observer(
  <T extends ZodString | ZodEnum<any>>(props: CheckboxInputProps<T>) => {
    const { field, value, label, classes: customClasses = {}, disabled } = props;

    const classes = {
      ...useCommonStyles(),
      ...useStyles(),
    };

    const form = useMemo(() => getForm(field), [field]);

    const checkedValues = getDecodeResultOr(field.decodeResult, [] as T['_input'][]);

    const checked = checkedValues.includes(value);

    const hasErrorMessage = field.errorMessages.length > 0 && field.touched;

    const onClick = () => {
      field.setTouched(true);
      if (checked) {
        field.setOutput(checkedValues.filter((v) => v !== value));
      } else {
        field.setOutput([...checkedValues, value]);
      }
    };

    return (
      <div
        className={clsx(
          classes.checkboxInputContainer,
          customClasses.container,
          disabled && classes.disabled,
        )}
        onClick={disabled ? () => {} : onClick}
      >
        <span className={clsx(classes.checkbox, disabled && classes.disabled)}>
          <CheckboxTick
            checked={checked}
            error={hasErrorMessage}
            style={{ height: 'inherit', width: 'inherit' }}
          />
        </span>
        <input
          className={clsx(classes.checkboxHidden, customClasses.checkbox)}
          checked={checked}
          disabled={disabled}
          {...form.bindField(field, { type: 'checkbox' })}
        />

        {label}

        <span style={{ display: 'inline-block', width: 6 }} />
      </div>
    );
  },
);
